// PaperApiConnect.tsx
// Phase 7 (2026-05-25) — Paper-only API connection UI. Demonstrates the
// required surface; full backend wiring activates in Phase 17.
//
// Hard rules (must hold for every render path):
//   - Only paper/sandbox brokers selectable from the dropdown.
//   - Required checkbox: "I confirm this is a paper/sandbox account and
//     does not provide real-money trading access."
//   - "Paper / sandbox API keys only" copy visible.
//   - "Live brokerage keys are not accepted" copy visible.
//   - "Real-money trading is disabled for this account" copy visible.
//   - After save, the secret is masked. NEVER re-display the raw secret.
//   - Demo and pre-approval users see a locked surface.

import React, { useState } from 'react';
import { Key, ShieldCheck, AlertTriangle, Lock } from 'lucide-react';
import { useAuthenticatedUser, NO_PAPER_ACCOUNT_COPY } from '../hooks/useAuthenticatedUser';
import { MockupPreviewBanner } from './MockupPreviewBanner';

const APPROVED_BROKERS: { id: string; label: string }[] = [
  { id: 'alpaca_paper',  label: 'Alpaca Paper' },
  { id: 'oanda_practice', label: 'OANDA Practice' },
];

export default function PaperApiConnect() {
  const u = useAuthenticatedUser();
  const allowed = u.permissions.api_integrations === true;

  const [brokerId, setBrokerId] = useState<string>('alpaca_paper');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [ack, setAck] = useState(false);
  const [saved, setSaved] = useState<{ broker_id: string; redactedKey: string; redactedSecret: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!allowed) {
    return (
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm"
           data-testid="paper-api-connect-locked">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={18} className="text-[var(--muted-text)]" />
          <h3 className="text-lg font-black uppercase italic">Paper API Connection</h3>
        </div>
        <div className="p-6 border-2 border-dashed border-[var(--input-border)] rounded-xl text-center space-y-2">
          <div className="text-sm font-black uppercase italic text-[var(--app-text)]">
            {NO_PAPER_ACCOUNT_COPY.heading}
          </div>
          <div className="text-xs font-bold text-[var(--muted-text)]">
            Paper / sandbox API keys can be added after an Adversyn admin approves your paper trading account.
          </div>
          <div className="text-[10px] font-black uppercase tracking-widest text-rose-500">
            Live brokerage keys are not accepted.
          </div>
        </div>
      </div>
    );
  }

  function attemptSave() {
    setError(null);
    // Lightweight frontend mirror of the backend `validate_paper_only_payload`.
    // Real validation happens server-side via /api/v1/paper-api/connect.
    if (!brokerId) { setError('Choose a broker.'); return; }
    if (!APPROVED_BROKERS.find((b) => b.id === brokerId)) {
      setError('Live brokerage keys are not accepted.'); return;
    }
    if (!ack) {
      setError('You must confirm this is a paper/sandbox account.'); return;
    }
    if (!apiKey || !apiSecret) {
      setError('API key and secret are required.'); return;
    }
    // Mock save — emulate the backend redaction
    const redactedKey = '****' + apiKey.slice(-4);
    const redactedSecret = '****' + apiSecret.slice(-4);
    setSaved({ broker_id: brokerId, redactedKey, redactedSecret });
    // Clear plaintext from state IMMEDIATELY (never persist to localStorage either)
    setApiKey('');
    setApiSecret('');
  }

  function disconnect() {
    setSaved(null);
    setAck(false);
  }

  return (
    <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm space-y-4"
         data-testid="paper-api-connect">
        <MockupPreviewBanner screen='paper-api-connect' />
      <div className="flex items-center gap-2">
        <Key size={18} className="text-emerald-600" />
        <h3 className="text-lg font-black uppercase italic">Paper API Connection</h3>
      </div>

      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs font-bold text-amber-700 dark:text-amber-300"
           data-testid="paper-only-banner">
        Paper / sandbox API keys only. Live brokerage keys are not accepted. Real-money trading is disabled for this account.
      </div>

      {saved ? (
        <div className="space-y-3" data-testid="paper-api-saved">
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs font-bold text-emerald-700">
            Paper API connection saved.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-[var(--input-bg)] border border-[var(--input-border)] p-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">Broker</div>
              <div className="text-sm font-bold text-[var(--app-text)]">{saved.broker_id}</div>
            </div>
            <div className="rounded-xl bg-[var(--input-bg)] border border-[var(--input-border)] p-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">API key</div>
              <code className="font-mono text-xs text-[var(--app-text)]" data-testid="redacted-api-key">{saved.redactedKey}</code>
            </div>
            <div className="rounded-xl bg-[var(--input-bg)] border border-[var(--input-border)] p-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">API secret</div>
              <code className="font-mono text-xs text-[var(--app-text)]" data-testid="redacted-api-secret">{saved.redactedSecret}</code>
              <div className="text-[10px] text-[var(--muted-text)] font-bold mt-1">Secret is never re-displayed after save.</div>
            </div>
          </div>
          <button
            onClick={disconnect}
            data-testid="paper-api-disconnect"
            className="rounded-xl bg-rose-600 hover:bg-rose-500 px-4 py-2 text-xs font-black uppercase italic text-white"
          >
            Disconnect
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="paper-api-form">
          <label className="block">
            <span className="block mb-1 text-xs font-black uppercase tracking-tight italic text-[var(--app-text)]">Broker</span>
            <select
              data-testid="paper-api-broker"
              value={brokerId}
              onChange={(e) => setBrokerId(e.target.value)}
              className="w-full rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] px-3 py-2 text-sm text-[var(--app-text)]"
            >
              {APPROVED_BROKERS.map((b) => (
                <option key={b.id} value={b.id}>{b.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block mb-1 text-xs font-black uppercase tracking-tight italic text-[var(--app-text)]">Paper API key</span>
            <input
              data-testid="paper-api-key-input"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              autoComplete="off"
              className="w-full rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] px-3 py-2 text-sm tabular-nums"
            />
          </label>
          <label className="block">
            <span className="block mb-1 text-xs font-black uppercase tracking-tight italic text-[var(--app-text)]">Paper API secret</span>
            <input
              data-testid="paper-api-secret-input"
              type="password"
              value={apiSecret}
              onChange={(e) => setApiSecret(e.target.value)}
              autoComplete="off"
              className="w-full rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] px-3 py-2 text-sm tabular-nums"
            />
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={ack}
              onChange={(e) => setAck(e.target.checked)}
              data-testid="paper-ack-checkbox"
              className="mt-0.5"
            />
            <span className="text-xs font-bold text-[var(--app-text)]" data-testid="paper-ack-copy">
              I confirm this is a paper/sandbox account and does not provide real-money trading access.
            </span>
          </label>
          {error && (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-2 text-xs font-bold text-rose-700"
                 data-testid="paper-api-error">
              {error}
            </div>
          )}
          <button
            data-testid="paper-api-save"
            onClick={attemptSave}
            className="rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 py-2 text-xs font-black uppercase italic text-white"
          >
            Save paper API connection
          </button>
        </div>
      )}

      <div className="text-[10px] text-[var(--muted-text)] font-bold uppercase tracking-widest"
           data-testid="paper-api-no-live-note">
        Real-money brokerage connections are not available. Live trading is disabled for this account.
      </div>
    </div>
  );
}
